/*
 * We want to be able to render only bars/parts of line that are visible to users,
 * but without introducing visual artifacts like bars missing in front of users while scrolling
 * or lines that start in middle of chart for sparse intraday stock data.
 *
 * In general we want to start rendering lines one bar sooner than what is visible to user and stop rendering
 * one bar outside of visible area to create a continuous line.
 */
import { ScaleAxis } from '../models/pane'
import type Quote from '../models/quote'

export const drawInVisibleArea = ({
  quote,
  scale,
  fxOverride,
  fromIndexOffset = 0,
  toIndexOffset = 0,
  leftOffset,
  drawBarCallback,
  width,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  fromIndexOffset?: number
  toIndexOffset?: number
  fxOverride?: (index: number) => number
  leftOffset: number
  width: number
  drawBarCallback: (i: number, x: number) => void
}) => {
  const fx = fxOverride ?? ((index: number) => scale.x(quote.barIndex[index]))

  // We want to start rendering one bar outside of the visible area for two reasons:
  // 1. To create a seamless transition for bars entering/leaving the view, avoiding them from
  //    abruptly dis/appearing when half the bar is outside.
  // 2. To prevent disconnected lines in case there's a bar gap.
  // However, if a `fromIndex` is provided and it's higher than the first visible bar,
  // we need to start rendering from that specific index. This ensures correct starting
  // points for elements like indicators that rely on this value.
  const firstBarToRenderIndex = getCompensatedFirstBarToRenderIndex({ quote, scale, leftOffset, fromIndexOffset })

  let lastBarToRender = -1
  for (let i = firstBarToRenderIndex; i < quote.close.length + toIndexOffset; i++) {
    const x = fx(i)
    if (x + leftOffset > width) {
      lastBarToRender = i
      break
    }
    drawBarCallback(i, x)
  }

  if (lastBarToRender > -1) {
    const x = fx(lastBarToRender)
    drawBarCallback(lastBarToRender, x)
  }
}

export const getFirstBarToRenderIndex = ({
  leftOffset,
  quote,
  scale,
  compensateBarGap = true,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  leftOffset: number
  compensateBarGap?: boolean
}) => {
  const barIndex = Math.ceil(scale.x.invert(-leftOffset))
  const calculatedIndex = quote.barToDataIndex[Math.max(barIndex, 0)]
  const isBarGap = scale.x(quote.barIndex[calculatedIndex]) + leftOffset < 0

  // not all the time there is bar present on barIndex calculated via inverted leftOffset
  // there might be a gap between bars, if there is, barToDataIndex on that gap spot will have value of
  // previous bar which isn't visible, if compensateBarGap === true we need to check if that happend
  // and correct it if so by increasing calculated index by 1

  return { index: compensateBarGap && isBarGap ? calculatedIndex + 1 : calculatedIndex, isBarGap }
}

export const getLastBarToRenderIndex = ({
  leftOffset,
  quote,
  scale,
  chartWidth,
  compensateBarGap = true,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  leftOffset: number
  chartWidth: number
  compensateBarGap?: boolean
}) => {
  const { barToDataIndex } = quote
  const barIndex = Math.ceil(scale.x.invert(-leftOffset + chartWidth))
  const calculatedIndex = barToDataIndex[Math.max(barIndex, 0)] ?? barToDataIndex[barToDataIndex.length - 1]
  const isBarGap = scale.x(quote.barIndex[calculatedIndex]) + leftOffset < 0

  return { index: compensateBarGap && isBarGap ? calculatedIndex + 1 : calculatedIndex, isBarGap }
}

// Get first bar to render compensated for fromIndexOffset
export function getCompensatedFirstBarToRenderIndex({
  quote,
  scale,
  leftOffset,
  fromIndexOffset = 0,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  fromIndexOffset?: number
  leftOffset: number
}) {
  return Math.max(0, getFirstBarToRenderIndex({ quote, scale, leftOffset }).index - 1, fromIndexOffset)
}
